import { useEffect } from 'react';
import {
  InteractionsResponse,
  useLazyGetInteractionsByTripByTripIdAndLinkSecretQuery,
  usePostTripsByTripIdUrlAndLinkSecretMutation,
} from '../../store/backendApi';

interface UseTripInteractionsReturn {
  interactions: InteractionsResponse | undefined;
  isLoading: boolean;
}

/**
 * Hook to manage trip interactions (likes, comments, views).
 * Platform-agnostic: Works in both React and React Native.
 *
 * @param tripId - The trip ID to load interactions for
 * @param linkSecret - Optional link secret for private trip access
 * @param userId - Current user ID (for authenticated tracking)
 * @returns Interactions data and loading state
 */
export const useTripInteractions = (
  tripId?: string,
  linkSecret?: string | null,
  userId?: string,
): UseTripInteractionsReturn => {
  const [loadInteractions, { data: interactions, isLoading }] =
    useLazyGetInteractionsByTripByTripIdAndLinkSecretQuery();
  const [saveTripUrl] = usePostTripsByTripIdUrlAndLinkSecretMutation();

  // Load interactions when trip data is available
  useEffect(() => {
    if (!tripId) {
      return;
    }
    loadInteractions({
      tripId,
      linkSecret: linkSecret ?? 'none',
    });
  }, [tripId, linkSecret, loadInteractions]);

  // Save trip URL for authenticated users with link secret
  useEffect(() => {
    if (!tripId || !linkSecret || !userId) {
      return;
    }
    saveTripUrl({
      tripId,
      linkSecret,
    });
  }, [tripId, linkSecret, userId, saveTripUrl]);

  return {
    interactions,
    isLoading,
  };
};
